
import React, { useState } from 'react';
import SectionSelector from './SectionSelector';
import FormInput from './components/FormInput';
import FormTextarea from './components/FormTextarea';
import AddButton from './components/AddButton';
import RemoveButton from './components/RemoveButton';
import WebsiteLinksInput from './components/WebsiteLinksInput';
import ExperiencesInput from './components/ExperiencesInput';

const ExecutiveResumeForm = ({ resumeData, onUpdate, validationErrors = {} }) => {
  const [activeSection, setActiveSection] = useState('contact');
  const [newSkill, setNewSkill] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [newWebsite, setNewWebsite] = useState("");
  const [newProject, setNewProject] = useState({ title: "", description: "", link: "", year: "", technologies: "" });

  const updateField = (field, data) => {
    onUpdate(prev => ({ ...prev, [field]: data }));
  };

  const handlePersonalChange = (field, value) => {
    onUpdate(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        const result = await response.json();
        handlePersonalChange('profile_image_url', result.file_url || URL.createObjectURL(file));
      } else {
        handlePersonalChange('profile_image_url', URL.createObjectURL(file));
      }
    } catch (error) {
      handlePersonalChange('profile_image_url', URL.createObjectURL(file));
    }
    setIsUploading(false);
  };





  const addProject = () => {
    if (newProject.title.trim()) {
      const projectToAdd = {
        title: newProject.title,
        description: newProject.description ? [newProject.description] : [],
        year: newProject.year || "",
        tech_stack: newProject.technologies ? newProject.technologies.split(',').map(t => t.trim()) : [],
        github_link: newProject.link || ""
      };
      updateField("projects", [...(resumeData.projects || []), projectToAdd]);
      setNewProject({ title: "", description: "", link: "", year: "", technologies: "" });
    }
  };

  const updateProject = (index, field, value) => {
    const updated = [...(resumeData.projects || [])];
    updated[index] = { ...updated[index], [field]: value };
    
    // Maintain data consistency across templates
    if (field === 'technologies') {
      updated[index].tech_stack = Array.isArray(value) ? value : value.split(',').map(t => t.trim());
    }
    if (field === 'tech_stack') {
      updated[index].technologies = Array.isArray(value) ? value : value.split(',').map(t => t.trim());
    }
    if (field === 'link') {
      updated[index].github_link = value;
    }
    if (field === 'github_link') {
      updated[index].link = value;
    }
    
    updateField("projects", updated);
  };

  const addProjectDetail = (projIndex) => {
    const updated = [...(resumeData.project_works || [])];
    updated[projIndex].details.push("");
    updateField("project_works", updated);
  };

  const updateProjectDetail = (projIndex, detailIndex, value) => {
    const updated = [...(resumeData.project_works || [])];
    updated[projIndex].details[detailIndex] = value;
    updateField("project_works", updated);
  };

  const removeProjectDetail = (projIndex, detailIndex) => {
    const updated = [...(resumeData.project_works || [])];
    updated[projIndex].details.splice(detailIndex, 1);
    updateField("project_works", updated);
  };

  const removeProject = (index) => {
    updateField("projects", resumeData.projects.filter((_, i) => i !== index));
  };

  const addWebsite = () => {
    if (newWebsite.trim()) {
      updateField("websites", [...(resumeData.websites || []), newWebsite.trim()]);
      setNewWebsite("");
    }
  };
  const removeWebsite = (index) => {
    updateField("websites", resumeData.websites.filter((_, i) => i !== index));
  };

  // Experience functions - match preview data structure
  const getExperienceData = () => {
    return resumeData.relevant_experience || resumeData.experience || [];
  };

  const persistExperienceData = (updatedList = []) => {
    const normalized = (Array.isArray(updatedList) ? updatedList : []).map((item) => {
      const points = Array.isArray(item?.description_points) ? item.description_points : [];
      return { ...(item || {}), description_points: points, responsibilities: points };
    });

    onUpdate((prev) => ({
      ...prev,
      relevant_experience: normalized,
      experience: normalized,
    }));
  };

  // Education functions
  const addEducation = () => {
    updateField("education", [...(resumeData.education || []), {
      degree: "", institution: "", date: ""
    }]);
  };

  const updateEducation = (index, field, value) => {
    const updated = [...(resumeData.education || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("education", updated);
  };

  const removeEducation = (index) => {
    updateField("education", resumeData.education.filter((_, i) => i !== index));
  };

  // Certification functions
  const addCertification = () => {
    if (newSkill.trim()) {
      updateField("certifications", [...(resumeData.certifications || []), newSkill.trim()]);
      setNewSkill("");
    }
  };

  const removeCertification = (index) => {
    updateField("certifications", resumeData.certifications.filter((_, i) => i !== index));
  };

  // Skills functions - match preview data structure
  const getSkillsData = () => {
    return resumeData.key_skills || resumeData.skills || [];
  };

  const getSkillsField = () => {
    return resumeData.key_skills ? "key_skills" : "skills";
  };

  const addSkill = () => {
    if (newSkill.trim()) {
      const field = getSkillsField();
      updateField(field, [...getSkillsData(), newSkill.trim()]);
      setNewSkill("");
    }
  };

  const removeSkill = (index) => {
    const field = getSkillsField();
    updateField(field, getSkillsData().filter((_, i) => i !== index));
  };

  const sections = [
    { id: 'contact', label: 'Contact' },
    { id: 'summary', label: 'Summary' },
    { id: 'experience', label: 'Experience' },  
    { id: 'skills', label: 'Skills' },
    { id: 'education', label: 'Education' },
    { id: 'projects', label: 'Projects' },
    { id: 'certifications', label: 'Certifications' }
  ];

  return (
    <div className="resume-form" style={{marginTop: 0, paddingTop: 0}}>
      <div className="form-content" style={{marginTop: 0, paddingTop: 0}}>
        <div className="form-header">  
          <h2 className="form-title">Executive Resume Editor</h2>
        </div>
        <SectionSelector
          sections={sections}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />
        <div className="section-form">
          {activeSection === 'contact' && (
            <div className="tab-panel">
              <h3 className="section-title">Contact Information</h3>
              <div className="form-group">
                <label>Profile Picture</label>
                <div className="flex items-center gap-4">
                  {resumeData?.profile_image_url && (
                    <img src={resumeData.profile_image_url} alt="Profile" className="w-20 h-20 rounded-full object-cover" />
                  )}
                  <label className="upload-btn">
                    {isUploading ? 'Uploading...' : 'Upload Photo'}
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleImageUpload}
                      accept="image/*"
                      disabled={isUploading}
                    />
                  </label>
                </div>
              </div>
              <div className="form-group-grid">
                <div className={`form-group ${validationErrors.full_name ? 'error' : ''}`}>
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={resumeData?.full_name || ""}
                    onChange={(e) => handlePersonalChange("full_name", e.target.value)}
                    placeholder="John Doe"
                    maxLength="50"
                  />
                  {validationErrors.full_name && <p className="error-message">{validationErrors.full_name}</p>}
                </div>
                <div className={`form-group ${validationErrors.job_title ? 'error' : ''}`}>
                  <label>Job Title</label>
                  <input
                    type="text"
                    value={resumeData?.job_title || ""}
                    onChange={(e) => handlePersonalChange("job_title", e.target.value)}
                    placeholder="Senior Executive"
                    maxLength="50"
                  />
                  {validationErrors.job_title && <p className="error-message">{validationErrors.job_title}</p>}
                </div>
              </div>
              <div className="form-group-grid">
                <div className={`form-group ${validationErrors.phone ? 'error' : ''}`}>
                  <label>Phone Number</label>
                  <input
                    type="text"
                    value={resumeData?.phone || ""}
                    onChange={(e) => handlePersonalChange("phone", e.target.value)}
                    placeholder="+88 01817118571"
                    maxLength="20"
                  />
                  {validationErrors.phone && <p className="error-message">{validationErrors.phone}</p>}
                </div>
                <div className={`form-group ${validationErrors.email ? 'error' : ''}`}>
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={resumeData?.email || ""}
                    onChange={(e) => handlePersonalChange("email", e.target.value)}
                    placeholder="hr.thecvguy@gmail.com"
                    maxLength="60"
                  />
                  {validationErrors.email && <p className="error-message">{validationErrors.email}</p>}
                </div>
              </div>
              <div className="form-group-grid">
                <div className={`form-group ${validationErrors.linkedin ? 'error' : ''}`}>
                  <label>LinkedIn</label>
                  <input
                    type="text"
                    value={resumeData?.linkedin || ""}
                    onChange={(e) => handlePersonalChange("linkedin", e.target.value)}
                    placeholder="thecvguy"
                    maxLength="50"
                  />
                  {validationErrors.linkedin && <p className="error-message">{validationErrors.linkedin}</p>}
                </div>
                <div className={`form-group ${validationErrors.location ? 'error' : ''}`}>
                  <label>Location</label>
                  <input
                    type="text"
                    value={resumeData?.location || resumeData?.address || ""}
                    onChange={(e) => {
                      // Update the field that exists, prioritizing location
                      const field = resumeData?.location !== undefined ? "location" : "address";
                      handlePersonalChange(field, e.target.value);
                    }}
                    placeholder="Your Present Location"
                    maxLength="50"
                  />
                  {validationErrors.location && <p className="error-message">{validationErrors.location}</p>}
                </div>
              </div>
              <div className="form-group">
                <label>Address</label>
                <textarea
                  value={resumeData?.address || ""}
                  onChange={(e) => handlePersonalChange("address", e.target.value)}
                  placeholder="123 Anywhere St., Any City, ST 12345"
                  rows={3}
                  maxLength="200"
                />
              </div>
              <WebsiteLinksInput
                websites={resumeData.websites || []}
                newWebsite={newWebsite}
                setNewWebsite={setNewWebsite}
                onAdd={addWebsite}
                onRemove={removeWebsite}
              />
            </div>
          )}
          {activeSection === 'projects' && (
            <div className="tab-panel">
              <h3 className="section-title">Projects</h3>
              <div className="form-group">
                <label>Title</label>
                <input type="text" value={newProject.title} onChange={e => setNewProject({ ...newProject, title: e.target.value })} placeholder="Project Title" maxLength="100" />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={newProject.description} onChange={e => setNewProject({ ...newProject, description: e.target.value })} placeholder="Project Description" rows={2} maxLength={300} />
              </div>
              <div className="form-group">
                <label>GitHub Link</label>
                <input type="text" value={newProject.link} onChange={e => setNewProject({ ...newProject, link: e.target.value })} placeholder="https://github.com/yourproject" maxLength="100" />
              </div>
              <div className="form-group">
                <label>Year</label>
                <input type="text" value={newProject.year} onChange={e => setNewProject({ ...newProject, year: e.target.value })} placeholder="2025" maxLength="10" />
              </div>
              <div className="form-group">
                <label>Tech Stack (comma separated)</label>
                <input type="text" value={newProject.technologies} onChange={e => setNewProject({ ...newProject, technologies: e.target.value })} placeholder="React, Node.js, MongoDB" maxLength="100" />
              </div>
              <button type="button" onClick={addProject} className="add-btn-small">Add Project</button>
              <div className="skills-list flex flex-wrap gap-2" style={{ marginTop: '12px' }}>
                {(resumeData.projects || []).map((proj, idx) => (
                  <div key={idx} className="project-tag" style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '8px', marginBottom: '8px' }}>
                    {proj.title} <br />
                    <span>{Array.isArray(proj.description) ? proj.description.join(' ') : proj.description}</span> <br />
                    {proj.github_link && <a href={proj.github_link} target="_blank" rel="noopener noreferrer">{proj.github_link}</a>}<br />
                    {proj.year && <span>Year: {proj.year}</span>}<br />
                    {proj.tech_stack && proj.tech_stack.length > 0 && <span>Tech Stack: {proj.tech_stack.join(', ')}</span>}<br />
                    <button type="button" onClick={() => removeProject(idx)} className="remove-skill-btn">Remove</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'skills' && (
            <div className="tab-panel">
              <h3 className="section-title">Skills</h3>
              <div className="form-group">
                <label>Add Skill</label>
                <input
                  type="text"
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  placeholder="Enter a skill"
                  maxLength="100"
                />
                <button type="button" onClick={() => addSkill()} className="add-btn-small">Add</button>
              </div>
              <div className="skills-list flex flex-wrap gap-2">
                {getSkillsData().map((skill, idx) => (
                  <span key={idx} className="skill-tag">
                    {skill}
                    <button type="button" onClick={() => removeSkill(idx)} className="remove-skill-btn">&times;</button>
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {activeSection === 'summary' && (
            <div className="tab-panel">
              <h3 className="section-title">Professional Summary</h3>
              <div className="form-group">
                <label>Summary</label>
                <textarea
                  value={resumeData?.professional_summary || resumeData?.summary || ""}
                  onChange={(e) => handlePersonalChange("professional_summary", e.target.value)}
                  placeholder="Write a brief professional summary..."
                  rows={5}
                  maxLength="500"
                />
              </div>
            </div>
          )}

          {activeSection === 'experience' && (
            <div className="tab-panel">
              <ExperiencesInput
                experiences={getExperienceData()}
                onChange={persistExperienceData}
                errors={validationErrors.relevant_experience || validationErrors.experience}
                title="Work Experience"
                addLabel="Add Experience"
                showLocation={false}
                positionPlaceholder="Job Title"
                companyPlaceholder="Company Name"
                startDatePlaceholder="2020"
                endDatePlaceholder="Present"
                pointsLabel="Responsibilities"
                pointsPlaceholder="Describe your responsibility..."
                positionMaxLength={100}
                companyMaxLength={100}
                startDateMaxLength={20}
                endDateMaxLength={20}
                pointsMaxLength={200}
              />
            </div>
          )}

          {activeSection === 'education' && (
            <div className="tab-panel">
              <h3 className="section-title">Education</h3>
              <button type="button" onClick={addEducation} className="add-btn">Add Education</button>
              {(resumeData.education || []).map((edu, index) => (
                <div key={index} className="education-item" style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                  <div className="form-group">
                    <label>Degree</label>
                    <input
                      type="text"
                      value={edu.degree || ""}
                      onChange={(e) => updateEducation(index, "degree", e.target.value)}
                      placeholder="Bachelor of Science in..."
                      maxLength="100"
                    />
                  </div>
                  <div className="form-group">
                    <label>Institution</label>
                    <input
                      type="text"
                      value={edu.institution || ""}
                      onChange={(e) => updateEducation(index, "institution", e.target.value)}
                      placeholder="University Name"
                      maxLength="100"
                    />
                  </div>
                  <div className="form-group">
                    <label>Date</label>
                    <input
                      type="text"
                      value={edu.date || ""}
                      onChange={(e) => updateEducation(index, "date", e.target.value)}
                      placeholder="2020"
                      maxLength="20"
                    />
                  </div>
                  <button type="button" onClick={() => removeEducation(index)} className="remove-btn">Remove Education</button>
                </div>
              ))}
            </div>
          )}

          {activeSection === 'certifications' && (
            <div className="tab-panel">
              <h3 className="section-title">Certifications</h3>
              <div className="form-group">
                <label>Add Certification</label>
                <input
                  type="text"
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  placeholder="Enter certification name"
                  maxLength="100"
                />
                <button type="button" onClick={() => addCertification()} className="add-btn-small">Add</button>
              </div>
              <div className="skills-list flex flex-wrap gap-2">
                {(resumeData.certifications || []).map((cert, idx) => (
                  <span key={idx} className="skill-tag">
                    {typeof cert === 'string' ? cert : cert.name || cert}
                    <button type="button" onClick={() => removeCertification(idx)} className="remove-skill-btn">&times;</button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExecutiveResumeForm;
